 
import { Component, input, output, OnInit, effect } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntypedFormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-lt-autocomplete-t',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
  ],
  template: `
    @if(items() !== null && items()?.length > 0)
    {
      <mat-form-field appearance="fill">
        <mat-label>{{label()}}</mat-label>
        <input type="text"
          matInput
          [matAutocomplete]="auto"
          [formControl]="itemFormControl">
        <mat-autocomplete autoActiveFirstOption
            #auto="matAutocomplete"
            (optionSelected)="selectItem($event.option.value)"
            [displayWith]="displayFn">
            @for(option of filteredItems$ | async; track $index) {
              <mat-option [value]="option">
                {{itemDisplayFn(option)}}
              </mat-option>
            }
        </mat-autocomplete>
      </mat-form-field>
    } @else {
      {{noItemsMessage()}}
    }
  `,
  styles: '',
})
export class LtAutocompleteTComponent<T> implements OnInit {
  label = input<string>('Select');
  noItemsMessage = input<string>('No items available.');
  items = input<T[]>([]);
  overrideId = input<any>();
  overrideIdField = input<keyof T>();
  overrideItem = input<T>();

  displayWithFn = input<(item: T) => string>();
  itemTemplateFn = input<(item: T) => string>();
  showSelectedItemChange = input<boolean>(false);

  itemSelected = output<T>();
  loadingFailed = output<void>();
  itemSelectedFromOverride = output<T>();

  itemFormControl = new UntypedFormControl('');
  filteredItems$!: Observable<T[]>;

  constructor() {
    effect(() => {
      this.handleOverrideInputs();
    });
  }

  displayFn = (item: T): string => {
    const fn = this.displayWithFn();
    return item ? fn(item): '';
  };

  itemDisplayFn = (item: T): string => {
    const fn = this.itemTemplateFn();
    return item ? fn(item): '';
  };

  ngOnInit(): void {
    this.filteredItems$ = this.itemFormControl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value.toLowerCase() : this.displayWithFn()(value).toLowerCase()),
      map(name => this.filterOptions(name)),
    );
  }

  private handleOverrideInputs(): void {
    if (this.overrideId() && this.overrideIdField() && this.items().length > 0) {
      const match = this.items().find(x => x[this.overrideIdField()!] === this.overrideId());
      if (match) {
        this.itemFormControl.setValue(match);
        this.selectItemForOverride(match);
      } else {
        // clear the form control if no match found
        this.itemFormControl.setValue('');
      }
    } else if (this.overrideItem() != null) {
      this.itemFormControl.setValue(this.overrideItem());
      this.selectItemForOverride(this.overrideItem());
    } else if (this.overrideId() == null || this.overrideId() === undefined) {
      // clear the form control when override is cleared
      this.itemFormControl.setValue('');
    }
  }

  selectItem(item: T): void {
    this.itemSelected.emit(item);
  }

  selectItemForOverride(item: T): void {
    this.itemSelectedFromOverride.emit(item);
  }

  filterOptions(name: string): any[] {
    const filterValue = name.toLowerCase();
    return this.items().filter(option => this.displayWithFn()(option).toLowerCase().includes(filterValue));
  }
}
